import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { tenantsApi } from '@/lib/api';
import { Envelope, ArrowClockwise } from '@phosphor-icons/react';

export const SmtpSettingsForm = ({ tenantConfig, setTenantConfig }) => {
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState('587');
  const [smtpUsername, setSmtpUsername] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [smtpFromEmail, setSmtpFromEmail] = useState('');
  const [smtpFromName, setSmtpFromName] = useState('');
  const [smtpUseTls, setSmtpUseTls] = useState(true);
  const [smtpUseSsl, setSmtpUseSsl] = useState(false);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [testEmailRecipient, setTestEmailRecipient] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  useEffect(() => {
    if (tenantConfig) {
      if (tenantConfig.smtp_config) {
        setSmtpHost(tenantConfig.smtp_config.smtp_host || '');
        setSmtpPort(tenantConfig.smtp_config.smtp_port !== undefined ? String(tenantConfig.smtp_config.smtp_port) : '587');
        setSmtpUsername(tenantConfig.smtp_config.smtp_username || '');
        // The API never echoes the stored password back (see backend _mask_secret_config) —
        // "smtp_password": null means one's already set; leave the field blank either way
        // and submitting blank means "keep the existing password."
        setSmtpPassword('');
        setSmtpFromEmail(tenantConfig.smtp_config.smtp_from_email || '');
        setSmtpFromName(tenantConfig.smtp_config.smtp_from_name || '');
        setSmtpUseTls(tenantConfig.smtp_config.smtp_use_tls !== false);
        setSmtpUseSsl(!!tenantConfig.smtp_config.smtp_use_ssl);
      } else {
        setSmtpHost('');
        setSmtpPort('587');
        setSmtpUsername('');
        setSmtpPassword('');
        setSmtpFromEmail('');
        setSmtpFromName('');
        setSmtpUseTls(true);
        setSmtpUseSsl(false);
      }
    }
  }, [tenantConfig]);

  const brandColorAccent = tenantConfig?.brand_colour || '#002FA7';
  const smtpPasswordConfigured = tenantConfig?.smtp_config?.smtp_password === null;

  const handleSaveSmtp = async (e) => {
    e.preventDefault();
    if (!tenantConfig?.id) {
      toast.error('No tenant configuration loaded');
      return;
    }
    setSavingSmtp(true);
    try {
      const { data } = await tenantsApi.update(tenantConfig.id, {
        smtp_config: {
          smtp_host: smtpHost,
          smtp_port: parseInt(smtpPort) || 587,
          smtp_username: smtpUsername,
          smtp_password: smtpPassword,
          smtp_from_email: smtpFromEmail,
          smtp_from_name: smtpFromName,
          smtp_use_tls: smtpUseTls,
          smtp_use_ssl: smtpUseSsl,
        }
      });
      setTenantConfig(data);
      toast.success('SMTP configuration saved successfully!');
    } catch (err) {
      console.error('Failed to save SMTP settings:', err);
      toast.error(err.response?.data?.detail || 'Failed to save SMTP settings');
    } finally {
      setSavingSmtp(false);
    }
  };

  const handleSendTestEmail = async (e) => {
    e.preventDefault();
    if (!tenantConfig?.id) {
      toast.error('No tenant configuration loaded');
      return;
    }
    if (!testEmailRecipient) {
      toast.error('Please enter a recipient email address');
      return;
    }
    setSendingTestEmail(true);
    try {
      await tenantsApi.testEmail(tenantConfig.id, testEmailRecipient);
      toast.success(`Test email sent successfully to ${testEmailRecipient}!`);
    } catch (err) {
      console.error('Failed to send test email:', err);
      toast.error(err.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
        <div className="p-2 bg-primary/10 rounded-sm text-primary">
          <Envelope size={24} style={{ color: brandColorAccent }} />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-850">SMTP Email Settings</h2>
          <p className="text-sm text-slate-500">Configure email gateway credentials for transaction notifications</p>
        </div>
      </div>

      <form onSubmit={handleSaveSmtp} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* SMTP Host */}
          <div className="space-y-2">
            <Label htmlFor="smtpHost" className="text-xs font-bold uppercase tracking-wider text-slate-700">
              SMTP Host
            </Label>
            <Input
              id="smtpHost"
              placeholder="e.g. smtp.gmail.com"
              value={smtpHost}
              onChange={(e) => setSmtpHost(e.target.value)}
              className="h-11 border-slate-350 bg-white"
            />
          </div>

          {/* SMTP Port */}
          <div className="space-y-2">
            <Label htmlFor="smtpPort" className="text-xs font-bold uppercase tracking-wider text-slate-700">
              SMTP Port
            </Label>
            <Input
              id="smtpPort"
              placeholder="e.g. 587 or 465"
              value={smtpPort}
              onChange={(e) => setSmtpPort(e.target.value)}
              className="h-11 border-slate-350 bg-white"
            />
          </div>

          {/* SMTP Username */}
          <div className="space-y-2">
            <Label htmlFor="smtpUsername" className="text-xs font-bold uppercase tracking-wider text-slate-700">
              SMTP Username
            </Label>
            <Input
              id="smtpUsername"
              placeholder="e.g. user@example.com"
              value={smtpUsername}
              onChange={(e) => setSmtpUsername(e.target.value)}
              className="h-11 border-slate-350 bg-white"
            />
          </div>

          {/* SMTP Password */}
          <div className="space-y-2">
            <Label htmlFor="smtpPassword" className="text-xs font-bold uppercase tracking-wider text-slate-700">
              SMTP Password
            </Label>
            <Input
              id="smtpPassword"
              type="password"
              placeholder={smtpPasswordConfigured ? 'Already set — leave blank to keep it' : '••••••••'}
              value={smtpPassword}
              onChange={(e) => setSmtpPassword(e.target.value)}
              className="h-11 border-slate-350 bg-white"
            />
          </div>

          {/* From Email */}
          <div className="space-y-2">
            <Label htmlFor="smtpFromEmail" className="text-xs font-bold uppercase tracking-wider text-slate-700">
              From Email
            </Label>
            <Input
              id="smtpFromEmail"
              type="email"
              placeholder="e.g. noreply@example.com"
              value={smtpFromEmail}
              onChange={(e) => setSmtpFromEmail(e.target.value)}
              className="h-11 border-slate-350 bg-white"
            />
          </div>

          {/* From Name */}
          <div className="space-y-2">
            <Label htmlFor="smtpFromName" className="text-xs font-bold uppercase tracking-wider text-slate-700">
              From Name
            </Label>
            <Input
              id="smtpFromName"
              placeholder="e.g. Resolv Support"
              value={smtpFromName}
              onChange={(e) => setSmtpFromName(e.target.value)}
              className="h-11 border-slate-350 bg-white"
            />
          </div>

          {/* Security checkboxes */}
          <div className="col-span-1 md:col-span-2 flex items-center gap-6 py-2">
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={smtpUseTls}
                onChange={(e) => {
                  setSmtpUseTls(e.target.checked);
                  if (e.target.checked) setSmtpUseSsl(false);
                }}
                className="w-4.5 h-4.5 text-primary border-slate-300 rounded"
              />
              Use STARTTLS (Recommended for Port 587)
            </label>

            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={smtpUseSsl}
                onChange={(e) => {
                  setSmtpUseSsl(e.target.checked);
                  if (e.target.checked) setSmtpUseTls(false);
                }}
                className="w-4.5 h-4.5 text-primary border-slate-300 rounded"
              />
              Use SSL/TLS (Direct Connection, Port 465)
            </label>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pt-4 border-t border-slate-100 gap-4">
          {/* Inline test email sender */}
          <div className="flex items-center gap-2 w-full sm:max-w-sm">
            <Input
              type="email"
              placeholder="Enter recipient email..."
              value={testEmailRecipient}
              onChange={(e) => setTestEmailRecipient(e.target.value)}
              className="h-10 border-slate-350 bg-white text-xs flex-1"
            />
            <Button
              type="button"
              variant="outline"
              onClick={handleSendTestEmail}
              disabled={sendingTestEmail || !testEmailRecipient}
              className="h-10 text-xs font-bold whitespace-nowrap px-4"
            >
              {sendingTestEmail ? 'Sending Test...' : 'Send Test Email'}
            </Button>
          </div>

          <Button 
            type="submit" 
            disabled={savingSmtp}
            className="font-bold text-white shadow hover:opacity-90 px-6 py-2 h-11 w-full sm:w-auto"
            style={{ backgroundColor: brandColorAccent }}
          >
            {savingSmtp ? (
              <>
                <ArrowClockwise className="animate-spin mr-2" size={18} />
                Saving changes...
              </>
            ) : (
              'Save SMTP Configuration'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};
